/**
 * Phase 6C — /settings/billing
 *
 * Shows current subscription state + how to pay. Two payment paths:
 *   1. Stripe checkout (future PR — disabled with "coming soon" copy)
 *   2. Manual cash/ATM transfer (instructions + bank details)
 *
 * Reachable even when the clinic is `suspended` — the entire purpose
 * is to give the merchant a way to pay and unblock themselves.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronLeft,
  CreditCard,
  Info,
  Loader2,
  Receipt,
} from 'lucide-react'

import {
  billing,
  type PromoPreview,
  type SubscriptionPayment,
  type SubscriptionPricing,
  type SubscriptionStatus,
} from '../api/client'
import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import { ApiError } from '../api/types'

const STATE_TONE: Record<string, { label: string; tone: string }> = {
  active: { label: 'active', tone: 'text-emerald-600' },
  trialing: { label: 'trialing', tone: 'text-emerald-600' },
  comped: { label: 'comped', tone: 'text-emerald-600' },
  past_due: { label: 'past_due', tone: 'text-amber-600' },
  canceled: { label: 'canceled', tone: 'text-destructive' },
  suspended: { label: 'suspended', tone: 'text-destructive' },
  deleted: { label: 'deleted', tone: 'text-muted-foreground' },
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

export default function SettingsBilling() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [status, setStatus] = useState<SubscriptionStatus | null>(null)
  const [payments, setPayments] = useState<SubscriptionPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [paymentsErr, setPaymentsErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const s = await billing.getSubscriptionStatus()
        if (alive) setStatus(s)
      } catch {
        /* status endpoint is always reachable — only fails if unauth */
      }
      try {
        const list = await billing.listSubscriptionPayments()
        if (alive) setPayments(list)
      } catch (e) {
        // listSubscriptionPayments needs billing.subscription.read; if the
        // clinic is suspended (hard block) the gate may still 402 — that's
        // OK, just hide the history.
        if (e instanceof ApiError && (e.status === 402 || e.status === 403)) {
          if (alive) setPaymentsErr('not_available')
        }
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const tone = status ? STATE_TONE[status.effective_status ?? 'active']?.tone ?? '' : ''

  return (
    <div className="container max-w-3xl py-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => nav('/')}
        className="mb-4 text-muted-foreground"
      >
        <ChevronLeft className="size-3.5" />
        {t('billing.page.back')}
      </Button>

      <h1 className="mb-1 text-2xl font-semibold">{t('billing.page.title')}</h1>
      <p className="mb-6 text-sm text-muted-foreground">{t('billing.page.sub')}</p>

      {/* Current state */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="size-4" />
            {t('billing.page.currentPlan')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('loading')}
            </div>
          ) : status ? (
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('billing.page.tier')}
                </dt>
                <dd className="font-medium">{status.tier ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('billing.page.status')}
                </dt>
                <dd className={`font-medium ${tone}`}>
                  {t(`billing.state.${status.effective_status ?? 'active'}`)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('billing.page.paidThrough')}
                </dt>
                <dd className="font-medium">{fmtDate(status.paid_through_at)}</dd>
              </div>
              {status.trial_ends_at && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('billing.page.trialEnds')}
                  </dt>
                  <dd className="font-medium">{fmtDate(status.trial_ends_at)}</dd>
                </div>
              )}
              {status.grace_days_remaining > 0 && (
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <AlertTriangle className="size-4" />
                    {t('billing.page.graceLeft', {
                      days: status.grace_days_remaining,
                    })}
                  </div>
                </div>
              )}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">{t('billing.page.unknown')}</p>
          )}
        </CardContent>
      </Card>

      {/* Pay now options */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-4" />
            {t('billing.page.payOptions')}
          </CardTitle>
          <CardDescription>{t('billing.page.payOptionsSub')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StripeCheckoutCard
            statusLoaded={!loading}
            autoCheckoutBlocked={
              !!status &&
              (status.effective_status === 'active' ||
                status.effective_status === 'comped' ||
                (status.effective_status === 'trialing' &&
                  status.paid_through_at !== null))
            }
          />


          <div className="rounded-md border border-border px-4 py-4">
            <h3 className="mb-2 text-sm font-semibold">
              {t('billing.page.manualTitle')}
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              {t('billing.page.manualInstructions')}
            </p>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t('billing.page.bankName')}</dt>
                <dd className="font-mono">{t('billing.page.bankNameValue')}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t('billing.page.bankAccount')}</dt>
                <dd className="font-mono">{t('billing.page.bankAccountValue')}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t('billing.page.bankBeneficiary')}</dt>
                <dd className="font-mono">{t('billing.page.bankBeneficiaryValue')}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">
              {t('billing.page.manualFootnote')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment history */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="size-4" />
            {t('billing.page.history')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          ) : paymentsErr ? (
            <p className="text-sm text-muted-foreground">{t('billing.page.historyHidden')}</p>
          ) : payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('billing.page.noHistory')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="pb-2 font-medium">{t('billing.page.colDate')}</th>
                  <th className="pb-2 font-medium">{t('billing.page.colPeriod')}</th>
                  <th className="pb-2 font-medium">{t('billing.page.colMethod')}</th>
                  <th className="pb-2 text-right font-medium">{t('billing.page.colAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="py-2">{fmtDate(p.created_at)}</td>
                    <td className="py-2 text-muted-foreground">
                      {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                    </td>
                    <td className="py-2">
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="size-3 text-emerald-600" />
                        {p.method}
                      </span>
                    </td>
                    <td className="py-2 text-right font-medium">
                      {p.currency} {p.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Stripe Checkout card with cycle toggle + promo input.
 *
 * Tiers + amounts fetched from /billing/subscription/pricing on
 * mount so display matches what Stripe actually charges. Annual
 * cycle hides itself when the backend reports no annual prices
 * configured for the product (graceful VetOS-only-annual launch
 * per Q2). Promo code pre-validates via /billing/subscription/
 * promo-validate; only valid codes pass through to checkout.
 */
type Cycle = 'monthly' | 'annual'

// owner-web is the PetPro merchant owner cockpit (owner.petpro.com.tw),
// so subscription billing is for the 'petpro' product (tiers solo/small/
// pro, each a 30-day Stripe trial). Single source of truth for the
// product on this page.
const PRODUCT = 'petpro'
const DEFAULT_TIER = 'solo'

function StripeCheckoutCard({
  statusLoaded = false,
  autoCheckoutBlocked = false,
}: {
  statusLoaded?: boolean
  autoCheckoutBlocked?: boolean
}) {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  // Guards the express checkout (landing → ?plan=…) so it fires once.
  const autoFiredRef = useRef(false)
  const [pricing, setPricing] = useState<SubscriptionPricing | null>(null)
  const [pricingError, setPricingError] = useState<string | null>(null)
  const [tier, setTier] = useState<string>(DEFAULT_TIER)
  const [cycle, setCycle] = useState<Cycle>('monthly')
  const [promoCode, setPromoCode] = useState<string>('')
  const [promoPreview, setPromoPreview] = useState<PromoPreview | null>(null)
  const [promoChecking, setPromoChecking] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    billing
      .getSubscriptionPricing(PRODUCT)
      .then((p) => {
        if (!alive) return
        setPricing(p)
        // Advance to the first tier that has a configured price.
        const firstConfigured = p.tiers.find((t) => t.monthly_amount !== null)
        if (firstConfigured) setTier(firstConfigured.tier)
      })
      .catch(() => {
        if (alive) setPricingError('pricing_fetch_failed')
      })
    return () => {
      alive = false
    }
  }, [])

  // Express checkout from the landing CTA: ?plan=solo|small|pro preselects
  // the tier and launches Stripe Checkout once — but only after the
  // subscription status is known (no race) AND only if the clinic isn't
  // already on a paid/trialing subscription (no double-subscribe). A 503
  // (prices unconfigured) flips start() to the graceful fallback instead.
  useEffect(() => {
    if (autoFiredRef.current || !pricing || !statusLoaded) return
    const planParam = searchParams.get('plan')
    if (!planParam) return
    const configured = pricing.tiers.some(
      (tt) => tt.tier === planParam && tt.monthly_amount !== null,
    )
    if (!configured) return
    setTier(planParam)
    if (!autoCheckoutBlocked) {
      autoFiredRef.current = true
      void start(planParam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing, statusLoaded, autoCheckoutBlocked])

  // Has any tier configured an annual price? If not, hide the toggle.
  const annualConfigured =
    pricing?.tiers.some((t) => t.annual_amount !== undefined) ?? false

  // If user switches to annual and the current tier doesn't have one,
  // snap back to monthly.
  useEffect(() => {
    if (cycle === 'annual' && pricing) {
      const current = pricing.tiers.find((t) => t.tier === tier)
      if (current && current.annual_amount === undefined) {
        setCycle('monthly')
      }
    }
  }, [cycle, tier, pricing])

  // Clear stale promo preview when (tier, cycle) change — discount
  // scope may differ.
  useEffect(() => {
    setPromoPreview(null)
    setPromoError(null)
  }, [tier, cycle])

  async function validatePromo() {
    const code = promoCode.trim().toUpperCase()
    if (!code) return
    setPromoChecking(true)
    setPromoError(null)
    setPromoPreview(null)
    try {
      const preview = await billing.validatePromoCode(code, PRODUCT, tier, cycle)
      setPromoPreview(preview)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setPromoError('promo_invalid')
      } else if (e instanceof ApiError && e.status === 429) {
        setPromoError('promo_rate_limit')
      } else {
        setPromoError('promo_error')
      }
    } finally {
      setPromoChecking(false)
    }
  }

  async function start(tierArg?: string) {
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      const result = await billing.startStripeCheckout(tierArg ?? tier, {
        billingCycle: cycle,
        discountCode: promoPreview ? promoCode.trim().toUpperCase() : undefined,
        product: PRODUCT,
      })
      window.location.assign(result.url)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 503) {
          setUnavailable(true)
        } else {
          setErr(e.message)
        }
      } else {
        setErr('errors.generic')
      }
    } finally {
      setBusy(false)
    }
  }

  if (unavailable) {
    return (
      <div className="rounded-md border border-border bg-secondary/40 px-4 py-3">
        <div className="mb-1 flex items-center gap-2 text-sm font-medium">
          <Info className="size-4 text-muted-foreground" />
          {t('billing.page.stripeTitle')}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('billing.page.stripeComingSoon')}
        </p>
      </div>
    )
  }

  const tierLabel = (tierKey: string) => {
    const key = `billing.page.tier${tierKey.charAt(0).toUpperCase()}${tierKey.slice(1)}`
    return t(key, tierKey.charAt(0).toUpperCase() + tierKey.slice(1))
  }

  return (
    <div className="rounded-md border border-border px-4 py-4">
      <div className="mb-2 flex items-center gap-2">
        <CreditCard className="size-4" />
        <h3 className="text-sm font-semibold">{t('billing.page.stripeTitle')}</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {t('billing.page.stripeSub', 'Pay by credit card — instant activation.')}
      </p>

      {/* Cycle toggle — hidden if annual isn't configured anywhere */}
      {annualConfigured && (
        <div className="mb-3 inline-flex rounded-md border border-border bg-secondary/30 p-0.5">
          {(['monthly', 'annual'] as Cycle[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              disabled={busy}
              className={
                cycle === c
                  ? 'rounded px-3 py-1 text-xs font-medium bg-background shadow-sm'
                  : 'rounded px-3 py-1 text-xs text-muted-foreground hover:text-foreground'
              }
            >
              {t(`billing.page.cycle${c.charAt(0).toUpperCase() + c.slice(1)}`, c)}
              {c === 'annual' && (
                <span className="ml-1.5 inline-block rounded-sm bg-emerald-100 px-1 text-[10px] font-medium text-emerald-700">
                  {t('billing.page.saveAnnualBadge', 'Save ~17%')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Tier grid */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        {(pricing?.tiers ?? []).map((tt) => {
          const monthly = tt.monthly_amount
          const annual = tt.annual_amount
          const isAvailable =
            cycle === 'monthly' ? monthly !== null : annual !== undefined
          const displayAmount: number | null =
            cycle === 'monthly'
              ? monthly
              : annual !== undefined && tt.annual_monthly_equivalent !== undefined
                ? tt.annual_monthly_equivalent
                : null
          const selected = tier === tt.tier
          return (
            <button
              key={tt.tier}
              type="button"
              onClick={() => isAvailable && setTier(tt.tier)}
              disabled={busy || !isAvailable}
              className={
                (selected && isAvailable
                  ? 'border-primary bg-primary text-primary-foreground '
                  : isAvailable
                    ? 'border-border bg-card hover:bg-secondary/60 '
                    : 'border-border bg-muted/40 text-muted-foreground cursor-not-allowed ') +
                'rounded-md border px-3 py-3 text-left'
              }
            >
              <div className="text-sm font-medium">{tierLabel(tt.tier)}</div>
              {isAvailable && displayAmount !== null ? (
                <div className="mt-1 text-xs">
                  NT${displayAmount.toLocaleString()}/mo
                  {cycle === 'annual' && annual !== undefined && (
                    <span
                      className={
                        selected
                          ? 'block text-[10px] opacity-80'
                          : 'block text-[10px] text-muted-foreground'
                      }
                    >
                      NT${annual.toLocaleString()}/{t('billing.page.year', 'yr')}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {t('billing.page.notAvailable', 'Not available')}
                </div>
              )}
              {/* Feature bullets — only render when admin has set them
                  (empty array preserves the original compact card UI).
                  Picks the right locale; falls back to the other side
                  when one is empty so admin can defer one language. */}
              {tt.features && tt.features.length > 0 && (
                <ul
                  className={
                    'mt-2 space-y-0.5 text-[10px] leading-tight ' +
                    (selected && isAvailable
                      ? 'text-primary-foreground/90'
                      : 'text-muted-foreground')
                  }
                >
                  {tt.features.map((f, i) => {
                    const isZh = i18n.language?.toLowerCase().startsWith('zh')
                    const text = isZh ? (f.zh || f.en) : (f.en || f.zh)
                    if (!text) return null
                    return (
                      <li key={i} className="flex gap-1">
                        <span aria-hidden="true">•</span>
                        <span>{text}</span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </button>
          )
        })}
      </div>

      {/* Promo code input */}
      <div className="mb-3">
        <label className="mb-1 block text-xs font-medium">
          {t('billing.page.promoLabel', 'Promo code (optional)')}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value)
              setPromoPreview(null)
              setPromoError(null)
            }}
            disabled={busy || promoChecking}
            placeholder="SAVE20"
            className="flex-1 rounded-md border border-border bg-card px-3 py-1.5 text-sm uppercase placeholder:text-muted-foreground/60 placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary/40"
            maxLength={32}
          />
          <button
            type="button"
            onClick={validatePromo}
            disabled={busy || promoChecking || !promoCode.trim()}
            className="rounded-md border border-border bg-secondary/50 px-3 py-1.5 text-xs font-medium hover:bg-secondary"
          >
            {promoChecking
              ? t('billing.page.promoChecking', 'Checking…')
              : t('billing.page.promoApply', 'Apply')}
          </button>
        </div>
        {promoPreview && (
          <p className="mt-1.5 text-xs text-emerald-700">
            {t('billing.page.promoSavings', {
              defaultValue: 'You save NT${{amount}} ({{net}} after discount)',
              amount: promoPreview.preview.discount_amount.toLocaleString(),
              net: `NT$${promoPreview.preview.net_amount.toLocaleString()}`,
            })}
          </p>
        )}
        {promoError === 'promo_invalid' && (
          <p className="mt-1.5 text-xs text-destructive">
            {t('billing.page.promoInvalid', 'Code is invalid or not applicable.')}
          </p>
        )}
        {promoError === 'promo_rate_limit' && (
          <p className="mt-1.5 text-xs text-destructive">
            {t('billing.page.promoRateLimit', 'Too many tries — wait a minute.')}
          </p>
        )}
        {promoError === 'promo_error' && (
          <p className="mt-1.5 text-xs text-destructive">
            {t('billing.page.promoGenericError', 'Could not validate code.')}
          </p>
        )}
      </div>

      {pricingError && (
        <p className="mb-2 text-xs text-destructive">
          {t('billing.page.pricingFetchFailed', 'Could not load tiers — please refresh.')}
        </p>
      )}
      {err && <p className="mb-2 text-xs text-destructive">{err}</p>}

      <Button onClick={() => start()} disabled={busy || !pricing} className="w-full">
        <CreditCard className="size-3.5" />
        {busy
          ? t('billing.page.stripeRedirecting', 'Redirecting…')
          : t('billing.page.stripePay', 'Pay with credit card')}
      </Button>
    </div>
  )
}
